<script setup>
import { slugify } from '#common/utils/slugify'
import { getAlcoholPercentFilterFromMinMax } from '#wa/utils/filters/alcohol'
import { getDrinkingWindowFilterFromMinMax } from '#wa/utils/filters/drinkingWindow'
import {
  FLAVOR_VALUE_TO_LABEL_MAPPING,
  getFlavorFilterFromMinMax,
} from '#wa/utils/filters/flavor'
import { metadataOccasionsToFilters } from '#wa/utils/filters/occasions'
import { getCustomPointsFilterFromMinMax } from '#wa/utils/filters/points'
import { getCustomPriceFilterFromMinMax } from '#wa/utils/filters/price'

import Accordion from '#common/components/Accordion/Accordion'
import AccordionItem from '#common/components/Accordion/AccordionItem'

const props = defineProps({
  currentFilters: {
    type: Array,
    required: false,
    default: () => [],
  },
  useSelectedFilters: {
    type: Boolean,
    required: false,
    default: false,
  },
  excluded: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  categories: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  varietals: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  hideExtraVarietals: {
    type: Boolean,
    required: false,
    default: true,
  },
  foodPairings: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  vintages: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  expertReviewers: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  occasionSlugs: {
    type: Array,
    required: false,
    default: () => {
      return []
    },
  },
  useAllRegions: {
    type: Boolean,
    required: false,
    default: false,
  },
})
const { $store } = useNuxtApp()
const metadata = useMetadata()
const emit = defineEmits([
  'sliderCleared',
  'toggleFilter',
  'updateRegionFilters',
  'sliderChanged',
  'toggleHideExtraVarietals',
])

const varietalList = ref(null)

const selectedFilters = computed(() =>
  props.useSelectedFilters
    ? props.currentFilters.filter(
        (f) => !['ordering', 'page'].includes(f.category),
      )
    : [],
)

const allRegionsWithSelections = computed(
  () => $store.getters.allRegionsWithSelections,
)
const offers = computed(() => {
  return [
    {
      category: 'is_offer',
      name: 'Ships Immediately (The Store)',
      key: 0,
      slug: 'false',
      key_as_string: 'false',
    },
    {
      category: 'is_offer',
      name: 'Preorder (Limited Time Offer)',
      key: 1,
      slug: 'true',
      key_as_string: 'true',
    },
  ]
})
const wineTeamCollections = computed(() => {
  const wt = metadataOccasionsToFilters(
    'wineteam',
    metadata.occasions,
    props.occasionSlugs,
  )
  // We explicitly hide Robert Emery in the filter
  return wt.filter((x) => x.key !== 'robert-emery-favorites')
})
const collections = computed(() => {
  return metadataOccasionsToFilters(
    'store',
    metadata.occasions,
    props.occasionSlugs,
  )
})
const restaurantCollectionsSubtitle = computed(() => {
  const restaurantCollections = metadata.occasions.find(
    (x) => x.key === 'restaurant',
  )
  if (restaurantCollections) return restaurantCollections.title || ''
  return ''
})
const restaurantCollections = computed(() => {
  return metadataOccasionsToFilters(
    'restaurant',
    metadata.occasions,
    props.occasionSlugs,
  )
})
const priceRange = computed(() => {
  return (
    selectedFilters.value.find((x) => x.category === 'custom_price') || {
      from: undefined,
      to: undefined,
    }
  )
})
const drinkingWindow = computed(() => {
  return (
    selectedFilters.value.find((x) => x.category === 'drinking_window') || {
      from: undefined,
      to: undefined,
    }
  )
})
const drinkingWindowBoundries = computed(() => {
  return [moment().year(), moment().year() + 10]
})
const alcoholRange = computed(() => {
  return (
    selectedFilters.value.find((x) => x.category === 'alcohol_percent') || {
      from: undefined,
      to: undefined,
    }
  )
})
const pointRange = computed(() => {
  return (
    selectedFilters.value.find((x) => x.category === 'custom_points') || {
      from: undefined,
      to: undefined,
    }
  )
})
const fruitRange = computed(() => flavorRange('fruit'))
const oakRange = computed(() => flavorRange('oak'))
const bodyRange = computed(() => flavorRange('body'))
const sweetnessRange = computed(() => flavorRange('sweetness'))
const tanninRange = computed(() => flavorRange('tannin'))
const acidityRange = computed(() => flavorRange('acidity'))
const aromaRange = computed(() => flavorRange('aroma'))
const flavorProfileLabels = computed(() => FLAVOR_VALUE_TO_LABEL_MAPPING)

function isFilterCategorySelected(category) {
  return !!selectedFilters.value.some((f) => f.category === category)
}

function isFilterSelected(filter) {
  return !!selectedFilters.value.find((f) => {
    return f.category === filter.category && f.key === filter.key
  })
}

function getFilterKey(filter) {
  return `${filter.key}-${isFilterSelected(filter)}`
}

function flavorRange(style) {
  return (
    selectedFilters.value.find(
      (x) => x.category === 'flavor_profile' && x.key === style,
    ) || {
      category: 'flavor_profile',
      key: style,
      from: undefined,
      to: undefined,
    }
  )
}
function adjustAlcoholSlider(alcoholMin, alcoholMax) {
  const newFilter = getAlcoholPercentFilterFromMinMax(alcoholMin, alcoholMax)
  if (newFilter.from || newFilter.to) {
    emit('sliderChanged', newFilter)
  } else {
    emit('sliderCleared', newFilter)
  }
}
function adjustFlavorSlider(style, flavorMin, flavorMax) {
  const newFilter = getFlavorFilterFromMinMax(flavorMin, flavorMax, style)

  if (
    newFilter.from ||
    !(newFilter.to === undefined || newFilter.to === null || newFilter.to === 3)
  ) {
    emit('sliderChanged', newFilter)
  } else {
    emit('sliderCleared', newFilter)
  }
}
function adjustPriceSlider(priceMin, priceMax) {
  const newFilter = getCustomPriceFilterFromMinMax(priceMin, priceMax)

  if (
    newFilter.from ||
    !(newFilter.to === undefined || newFilter.to === null)
  ) {
    emit('sliderChanged', newFilter)
  } else {
    emit('sliderCleared', newFilter)
  }
}
function adjustPointSlider(pointMin, pointMax) {
  const newFilter = getCustomPointsFilterFromMinMax(pointMin, pointMax)

  if (newFilter.from || newFilter.to) {
    emit('sliderChanged', newFilter)
  } else {
    emit('sliderCleared', newFilter)
  }
}
function adjustDrinkingWindowSlider(windowMin, windowMax) {
  const newFilter = getDrinkingWindowFilterFromMinMax(windowMin, windowMax)

  if (newFilter.from || newFilter.to) {
    emit('sliderChanged', newFilter)
  } else {
    emit('sliderCleared', newFilter)
  }
}
function handleShowMoreVarietals(event) {
  emit('toggleHideExtraVarietals')
  nextTick(() => {
    varietalList.value.querySelector('li:first-child > input').focus()
  })
}
function handleFilterChange(categoryKey, item) {
  emit('toggleFilter', item)

  // Prevent unnecessary re-renders
  const checkboxId =
    categoryKey === 'offer' ? `offer-${item.key_as_string}` : slugify(item.key)

  const checkbox = document.getElementById(checkboxId)
  if (checkbox) {
    checkbox.checked = isFilterSelected(item)
  }
}
</script>

<template>
  <div class="filter-sidebar-card">
    <Accordion id="filter-accordions" :multiple="true">
      <AccordionItem
        title="Type"
        :expanded-by-default="isFilterCategorySelected('categories')"
      >
        <ul class="filter-set">
          <li v-for="category in categories" :key="getFilterKey(category)">
            <input
              :id="slugify(category.key)"
              type="checkbox"
              :default-checked="isFilterSelected(category)"
              :checked="isFilterSelected(category)"
              @change="handleFilterChange('category', category)"
            />
            <label :for="slugify(category.key)">{{ category?.name }}</label>
          </li>
        </ul>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('varietals')"
        title="Variety"
        :expanded-by-default="isFilterCategorySelected('varietals')"
      >
        <ul ref="varietalList" class="filter-set">
          <li v-for="varietal in varietals" :key="getFilterKey(varietal)">
            <input
              :id="slugify(varietal.key)"
              type="checkbox"
              :default-checked="isFilterSelected(varietal)"
              :checked="isFilterSelected(varietal)"
              @change="handleFilterChange('varietal', varietal)"
            />
            <label :for="slugify(varietal.key)">{{ varietal?.name }}</label>
          </li>
        </ul>
        <button
          class="button-link"
          :class="{
            'show-more-active': hideExtraVarietals,
            'show-less-active': !hideExtraVarietals,
          }"
          :aria-expanded="!hideExtraVarietals"
          @click.prevent="handleShowMoreVarietals"
        >
          <span v-if="hideExtraVarietals">Show More</span>
          <span v-else>Show Less</span>
        </button>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('regions')"
        title="Region"
        :expanded-by-default="isFilterCategorySelected('regions')"
      >
        <RegionsSideFilter
          v-if="useAllRegions"
          :regions="allRegionsWithSelections"
          :use-all-regions="useAllRegions"
          @change="emit('updateRegionFilters', $event)"
        />
        <RegionsSideFilter
          v-else
          :regions="metadata.regionsWithSelections"
          @change="emit('updateRegionFilters', $event)"
        />
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('availability')"
        title="Availability"
        :expanded-by-default="isFilterCategorySelected('availability')"
      >
        <ul class="filter-set">
          <li v-for="offer in offers" :key="getFilterKey(offer)">
            <input
              :id="`offer-${offer.key_as_string}`"
              type="checkbox"
              :default-checked="isFilterSelected(offer)"
              :checked="isFilterSelected(offer)"
              @change="handleFilterChange('offer', offer)"
            />
            <label :for="`offer-${offer.key_as_string}`">
              {{ offer?.name }}
            </label>
          </li>
        </ul>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('expert_reviewers')"
        title="Reviewed By"
        :expanded-by-default="isFilterCategorySelected('expert_reviewers')"
      >
        <ul class="filter-set">
          <li v-for="reviewer in expertReviewers" :key="getFilterKey(reviewer)">
            <input
              :id="slugify(reviewer.key)"
              type="checkbox"
              :default-checked="isFilterSelected(reviewer)"
              :checked="isFilterSelected(reviewer)"
              @change="handleFilterChange('reviewer', reviewer)"
            />
            <label :for="slugify(reviewer.key)">
              {{ reviewer?.name }}
            </label>
          </li>
        </ul>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('experts')"
        title="Wine Access Expert"
        :expanded-by-default="isFilterCategorySelected('experts')"
      >
        <ul class="filter-set">
          <li
            v-for="collection in wineTeamCollections"
            :key="getFilterKey(collection)"
          >
            <input
              :id="slugify(collection.key)"
              type="checkbox"
              :default-checked="isFilterSelected(collection)"
              :checked="isFilterSelected(collection)"
              @change="handleFilterChange('collection', collection)"
            />
            <label :for="slugify(collection.key)">
              {{ collection?.name }}
            </label>
          </li>
        </ul>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('food_pairings')"
        title="Food Pairing"
        :expanded-by-default="isFilterCategorySelected('food_pairings')"
      >
        <ul class="filter-set">
          <li v-for="food in foodPairings" :key="getFilterKey(food)">
            <input
              :id="slugify(food.key)"
              type="checkbox"
              :default-checked="isFilterSelected(food)"
              :checked="isFilterSelected(food)"
              @change="handleFilterChange('food', food)"
            />
            <label :for="slugify(food.key)">{{ food?.name }}</label>
          </li>
        </ul>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('price')"
        title="Price"
        :expanded-by-default="isFilterCategorySelected('price')"
      >
        <DualSliderFilter
          :key="`price-range-${slugify(priceRange?.name || 'custom_price')}`"
          input-prefix="price"
          :filter="priceRange"
          :boundaries="[1, 151]"
          min-range-label="Minimum Price"
          max-range-label="Maximum Price"
          :omit-min-boundary-from-filter="true"
          :omit-max-boundary-from-filter="true"
          @change="adjustPriceSlider"
        >
          <template #min-label>
            Min
            <span class="sr-only">Price</span>
            $
          </template>
          <template #max-label>
            Max
            <span class="sr-only">Price</span>
            $
          </template>
        </DualSliderFilter>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('points')"
        title="Points"
        :expanded-by-default="isFilterCategorySelected('points')"
      >
        <DualSliderFilter
          :key="`point-range-${slugify(pointRange?.name || 'custom_points')}`"
          input-prefix="points"
          :filter="pointRange"
          :boundaries="[89, 101]"
          min-range-label="Min Points"
          max-range-label="Max Points"
          :omit-min-boundary-from-filter="true"
          :omit-max-boundary-from-filter="true"
          @change="adjustPointSlider"
        >
          <template #min-label> Min Points </template>
          <template #max-label> Max Points </template>
        </DualSliderFilter>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('alcohol')"
        title="Alcohol %"
        :expanded-by-default="isFilterCategorySelected('alcohol')"
      >
        <!-- The boundary here is set to 25.001 so that the input of 25 can be a valid max -->
        <DualSliderFilter
          :key="`alcohol-${slugify(alcoholRange?.name || 'alcohol_percent')}`"
          input-prefix="alcohol"
          :filter="alcoholRange"
          :range-gap="0.25"
          :range-step="0.25"
          :boundaries="[0, 25.001]"
          min-range-label="Min %"
          max-range-label="Max %"
          :omit-min-boundary-from-filter="true"
          :omit-max-boundary-from-filter="true"
          @change="adjustAlcoholSlider"
        >
          <template #min-label>
            Min
            <span class="sr-only">Percent</span>
            %
          </template>
          <template #max-label>
            Max
            <span class="sr-only">Percent</span>
            %
          </template>
        </DualSliderFilter>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('vintage')"
        title="Vintage"
        :expanded-by-default="isFilterCategorySelected('vintage')"
      >
        <ul class="filter-set">
          <li v-for="vintage in vintages" :key="getFilterKey(vintage)">
            <input
              :id="vintage.key"
              type="checkbox"
              :default-checked="isFilterSelected(vintage)"
              :checked="isFilterSelected(vintage)"
              @change="handleFilterChange('vintage', vintage)"
            />
            <label :for="slugify(vintage.key)">
              {{ vintage.key }}
            </label>
          </li>
        </ul>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('drinking_window')"
        title="Drinking Window"
        :expanded-by-default="isFilterCategorySelected('drinking_window')"
      >
        <DualSliderFilter
          :key="`drinking-window-${slugify(
            drinkingWindow?.name || 'drinking_window',
          )}`"
          input-prefix="drinking_window"
          :filter="drinkingWindow"
          :boundaries="drinkingWindowBoundries"
          min-range-label="Min Year(s)"
          max-range-label="Max Year(s)"
          :omit-min-boundary-from-filter="true"
          :omit-max-boundary-from-filter="true"
          @change="adjustDrinkingWindowSlider"
        >
          <template #min-label> Min Year(s) </template>
          <template #max-label> Max Year(s) </template>
        </DualSliderFilter>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('flavor_profile')"
        title="Flavor Profile"
        :expanded-by-default="isFilterCategorySelected('flavor_profile')"
      >
        <fieldset>
          <div class="legend">
            <b>Fruit</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-fruit-${slugify(
              fruitRange?.name || 'flavor_profile_fruit',
            )}`"
            input-prefix="flavor_profile_fruit"
            :filter="fruitRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('fruit', min, max)"
          />
        </fieldset>
        <fieldset>
          <div class="legend">
            <b>Oak</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-oak-${slugify(
              oakRange?.name || 'flavor_profile_oak',
            )}`"
            input-prefix="flavor_profile_oak"
            :filter="oakRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('oak', min, max)"
          />
        </fieldset>
        <fieldset>
          <div class="legend">
            <b>Body</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-body-${slugify(
              bodyRange?.name || 'flavor_profile_body',
            )}`"
            input-prefix="flavor_profile_body"
            :filter="bodyRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('body', min, max)"
          />
        </fieldset>
        <fieldset>
          <div class="legend">
            <b>Sweetness</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-sweetness-${slugify(
              sweetnessRange?.name || 'flavor_profile_sweetness',
            )}`"
            input-prefix="flavor_profile_sweetness"
            :filter="sweetnessRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('sweetness', min, max)"
          />
        </fieldset>
        <fieldset>
          <div class="legend">
            <b>Tannin</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-tannin-${slugify(
              tanninRange?.name || 'flavor_profile_tannin',
            )}`"
            input-prefix="flavor_profile_tannin"
            :filter="tanninRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('tannin', min, max)"
          />
        </fieldset>
        <fieldset>
          <div class="legend">
            <b>Acidity</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-acidity-${slugify(
              acidityRange?.name || 'flavor_profile_acidity',
            )}`"
            input-prefix="flavor_profile_acidity"
            :filter="acidityRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('acidity', min, max)"
          />
        </fieldset>
        <fieldset>
          <div class="legend">
            <b>Aromatic Intensity</b>
          </div>
          <DualSliderFilter
            :key="`flavor-profile-aroma-${slugify(
              aromaRange?.name || 'flavor_profile_aroma',
            )}`"
            input-prefix="flavor_profile_aroma"
            :filter="aromaRange"
            :range-gap="0"
            :boundaries="[0, 3]"
            :show-text-fields="false"
            :show-value-labels="true"
            :value-label-mapping="flavorProfileLabels"
            :omit-min-boundary-from-filter="false"
            :omit-max-boundary-from-filter="false"
            @change="(min, max) => adjustFlavorSlider('aroma', min, max)"
          />
        </fieldset>
      </AccordionItem>
      <AccordionItem
        v-if="!excluded.includes('collections')"
        title="Collections"
        :expanded-by-default="isFilterCategorySelected('collections')"
      >
        <ul class="filter-set">
          <li v-for="collection in collections" :key="getFilterKey(collection)">
            <input
              :id="slugify(collection.key)"
              type="checkbox"
              :default-checked="isFilterSelected(collection)"
              :checked="isFilterSelected(collection)"
              @change="handleFilterChange('collection', collection)"
            />
            <label :for="slugify(collection.key)">
              {{ collection?.name }}
            </label>
          </li>
        </ul>
        <b v-if="restaurantCollectionsSubtitle && restaurantCollections">
          {{ restaurantCollectionsSubtitle }}
        </b>
        <ul v-if="restaurantCollections" class="filter-set">
          <li
            v-for="restaurantCollection in restaurantCollections"
            :key="getFilterKey(restaurantCollection)"
          >
            <input
              :id="slugify(restaurantCollection.key)"
              type="checkbox"
              :default-checked="isFilterSelected(restaurantCollection)"
              :checked="isFilterSelected(restaurantCollection)"
              @change="
                handleFilterChange('restaurantCollection', restaurantCollection)
              "
            />
            <label :for="slugify(restaurantCollection.key)">
              {{ restaurantCollection?.name }}
            </label>
          </li>
        </ul>
      </AccordionItem>
    </Accordion>
  </div>
</template>
